import React, { FC, useEffect } from 'react';

import {
    PieChartOutlined,
    LineChartOutlined,
    DotChartOutlined,
    AreaChartOutlined,
    BarChartOutlined,
    QuestionCircleOutlined,
    FunnelPlotOutlined,
    DashboardOutlined,
    AppstoreOutlined
} from '@ant-design/icons';
import { Button, Select, Space, Switch, Tooltip } from 'antd';

import { TrendType } from '../../../../../../app/providers/redux/slices/reports/chart/types';
import { ToggleChart } from '../../../../../../widgets/ToggleChart';
import { chartDescription, trendDescription } from '../../../../constants/tooltips';
import { useActions } from '../../../../hooks/useActions';
import { useTypedSelector } from '../../../../hooks/useTypedSelector';

// Tabler axis-y icon (https://tabler.io/icons/icon/axis-y)
const YAxisIcon: FC = () => (
    <span role="img" aria-label="y-axis" className="anticon">
        <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
            <path d="M11 20h-.01"/>
            <path d="M15 20h-.01"/>
            <path d="M19 20h-.01"/>
            <path d="M4 7l3 -3l3 3"/>
            <path d="M7 20v-16"/>
        </svg>
    </span>
);

// tdesign:chart-line-data — линейный тренд
const TrendLinearIcon: FC = () => (
    <span role="img" aria-label="trend-linear" className="anticon">
        <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none">
            <path d="M21 21H3V3" stroke="currentColor" strokeWidth="2" strokeLinecap="square"/>
            <path d="M8.8 13.75l9.4-5.49" stroke="currentColor" strokeWidth="2" strokeLinecap="square"/>
            <path d="M21 7.5a1.5 1.5 0 1 1-3 0a1.5 1.5 0 0 1 3 0Z" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="square"/>
            <path d="M8.25 15.798a1.5 1.5 0 1 1-1.5-2.598a1.5 1.5 0 0 1 1.5 2.598Z" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="square"/>
        </svg>
    </span>
);

// tdesign:chart-line-data-1 — MA (скользящее среднее)
const TrendMAIcon: FC = () => (
    <span role="img" aria-label="trend-ma" className="anticon">
        <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
            <path d="M21 21H3V3"/>
            <path d="M6.228 13.447s2.727-5.376 5.25-4.661c2.521.715 1.543 3.742 4.032 4.478c2.49.737 5.234-4.648 5.234-4.648"/>
        </svg>
    </span>
);

// tdesign:chart-median — средняя линия
const TrendMedianIcon: FC = () => (
    <span role="img" aria-label="trend-median" className="anticon">
        <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
            <path d="M21 21H3V3"/>
            <path d="M19 6.5c0-1.71-1.343-3.096-3-3.096S13 4.79 13 6.5v2m-6 6.07C7 16.465 8.343 18 10 18s3-1.535 3-3.429V12.5m3-1.998h-.004v-.004H16zm3.004 0H19v-.004h.004zm-9.004 0h-.004v-.004H10zM7.004 10.5H7v-.004h.004zm6 .002H13v-.004h.004z"/>
        </svg>
    </span>
);

const TREND_OPTIONS: Array<{ value: TrendType; label: React.ReactNode }> = [
    { value: 'linear', label: <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><TrendLinearIcon /> Линейный</span> },
    { value: 'average', label: <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><TrendMedianIcon /> Средняя</span> },
    { value: 'ma', label: <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><TrendMAIcon /> MA (авто)</span> },
    { value: 'ma7', label: <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><TrendMAIcon /> MA (7)</span> },
    { value: 'ma14', label: <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><TrendMAIcon /> MA (14)</span> },
    { value: 'ma30', label: <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><TrendMAIcon /> MA (30)</span> },
];

const ButtonsType: FC = () => {
    const { isCompare } = useTypedSelector((state) => state.meta);
    const { chartType, isChartVisible, isAxesVisible, showTrend, defaultTrendType, series } = useTypedSelector((state) => state.chart);
    const dataPointsCount = (series?.[0] as any)?.data?.length || 0;
    const { setChartType, setMetaChartFetching, setMetaCompareChartFetching, setAxesVisible, setShowTrend, setDefaultTrendType, resetMetricsTrendOverride } = useActions();

    const changeType = (type: string) => {

        setChartType(type);
        if (isCompare) {
            setTimeout(() => setMetaCompareChartFetching({ value: true }), 50);
        } else {
            setMetaChartFetching({ value: true });
        }
    };

    useEffect(() => {
        if (isCompare) {
            if (chartType === 'bar') {
                changeType('bar');
            } else if (chartType !== 'line') {
                changeType('line');
            }
        }
    }, [ isCompare ]);

    if (window.location.pathname === '/dashboards') {
        return null;
    }

    const handleToggleAxes = (checked: boolean) => {
        localStorage.setItem('isAxesVisible', `${checked}`);
        setAxesVisible();
    };

    const handleToggleTrend = (checked: boolean) => {
        setShowTrend(checked);
        if (checked) {
            // При включении глобального тренда сбрасываем per-metric переопределения,
            // чтобы все метрики снова следовали глобальной настройке
            resetMetricsTrendOverride(undefined);
        }
    };

    const handleTrendTypeChange = (value: TrendType) => {
        setDefaultTrendType(value);
    };

    // Тренд доступен только для line и area
    const isTrendSupported = chartType === 'line' || chartType === 'area';

    // Иконка текущего типа тренда
    const getTrendIcon = () => {
        if (!defaultTrendType || defaultTrendType === 'linear') return <TrendLinearIcon />;
        if (defaultTrendType === 'average') return <TrendMedianIcon />;
        return <TrendMAIcon />;
    };

    return (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 15, marginBottom: isChartVisible ? '15px' : '0' }}>
            <Space size={7} align="center">
                <span data-help-id="chart-toggle"><ToggleChart /></span>
                {isChartVisible && (
                    <>
                        <Button
                            data-help-id="chart-type-pie"
                            onClick={() => changeType('pie')}
                            icon={<PieChartOutlined />}
                            type={chartType === 'pie' ? 'primary' : 'text'}
                            title="Круговая"
                            disabled={isCompare}
                        />
                        <Button
                            data-help-id="chart-type-line"
                            onClick={() => changeType('line')}
                            icon={<LineChartOutlined />}
                            type={chartType === 'line' ? 'primary' : 'text'}
                            title="Линии"
                        />
                        <Button
                            data-help-id="chart-type-area"
                            onClick={() => changeType('area')}
                            icon={<AreaChartOutlined />}
                            type={chartType === 'area' ? 'primary' : 'text'}
                            title="Области"
                            disabled={isCompare}
                        />
                        <Button
                            data-help-id="chart-type-bar"
                            onClick={() => changeType('bar')}
                            icon={<BarChartOutlined />}
                            type={chartType === 'bar' ? 'primary' : 'text'}
                            title="Колонки"
                        />
                        <Button
                            data-help-id="chart-type-bubble"
                            onClick={() => changeType('bubble')}
                            icon={<DotChartOutlined />}
                            type={chartType === 'bubble' ? 'primary' : 'text'}
                            title="Пузырьковая"
                            disabled={isCompare}
                        />
                        <Button
                            data-help-id="chart-type-funnel"
                            onClick={() => changeType('tunnel')}
                            icon={<FunnelPlotOutlined />}
                            type={chartType === 'tunnel' ? 'primary' : 'text'}
                            title="Тунельная "
                            disabled={isCompare}
                        />
                        <Button
                            data-help-id="chart-type-gauge"
                            onClick={() => changeType('speedometer')}
                            icon={<DashboardOutlined />}
                            type={chartType === 'speedometer' ? 'primary' : 'text'}
                            title="Выберите метрику с указанным планом"
                            disabled={isCompare}
                        />
                        <Button
                            data-help-id="chart-type-treemap"
                            onClick={() => changeType('treemap')}
                            icon={<AppstoreOutlined />}
                            type={chartType === 'treemap' ? 'primary' : 'text'}
                            title="Древовидная карта"
                            disabled={isCompare}
                        />
                        <span data-help-id="chart-help">
                            <Tooltip
                                placement="bottom"
                                title={
                                    <div className="page-title-tooltip-text" dangerouslySetInnerHTML={{ __html: chartDescription }}></div>
                                }
                                color="#fff"
                                zIndex={9999}
                                trigger="click"
                                overlayClassName="page-title-tooltip"
                            >
                                <QuestionCircleOutlined style={{ color: '#ccc', fontSize: 14, marginLeft: 10 }} />
                            </Tooltip>
                        </span>
                    </>
                )}
            </Space>
            {isChartVisible && (
                <Space size={16} align="center">
                    {isTrendSupported && (
                        <span
                            data-help-id="chart-trend"
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 8,
                                color: '#595959',
                                fontSize: 14,
                                lineHeight: '16px',
                            }}
                        >
                            <Tooltip
                                placement="bottom"
                                title={
                                    <div className="page-title-tooltip-text" dangerouslySetInnerHTML={{ __html: trendDescription }}></div>
                                }
                                color="#fff"
                                zIndex={9999}
                                trigger="click"
                                overlayClassName="page-title-tooltip"
                            >
                                <QuestionCircleOutlined style={{ color: '#ccc', fontSize: 14, cursor: 'pointer' }} />
                            </Tooltip>
                            <span style={{ display: 'inline-flex', alignItems: 'center', lineHeight: '16px' }}>
                                Тренд и среднее
                            </span>
                            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                <Switch
                                    checked={showTrend}
                                    onChange={handleToggleTrend}
                                    size="small"
                                />
                            </span>
                            {showTrend && (
                                <Select
                                    value={defaultTrendType || 'linear'}
                                    onChange={handleTrendTypeChange}
                                    size="small"
                                    style={{ width: 120 }}
                                    options={TREND_OPTIONS.map(opt => ({
                                        ...opt,
                                        disabled: (opt.value === 'ma7' && dataPointsCount < 7) ||
                                                  (opt.value === 'ma14' && dataPointsCount < 14) ||
                                                  (opt.value === 'ma30' && dataPointsCount < 30),
                                    }))}
                                    bordered={false}
                                />
                            )}
                        </span>
                    )}
                    <span
                        data-help-id="chart-axis-y"
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 8,
                            color: '#595959',
                            fontSize: 14,
                            lineHeight: '16px',
                        }}
                    >
                        <span style={{ display: 'inline-flex', alignItems: 'center', lineHeight: '16px' }}>
                            Ось Y
                        </span>
                        <span
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                fontSize: 14,
                                lineHeight: '16px',
                            }}
                        >
                            <YAxisIcon />
                        </span>
                        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                            <Switch
                                checked={isAxesVisible}
                                onChange={handleToggleAxes}
                                size="small"
                            />
                        </span>
                    </span>
                </Space>
            )}
        </div>
    );
};

export default ButtonsType;
